import { useRef, useState } from "react";
import { ImagePlus, Loader2, Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";

const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Single-file image picker used by the onboarding Personal + Workspace
 * steps for avatar / logo. Two visual variants:
 *
 *   shape="circle"  → round preview (avatar)
 *   shape="square"  → dashed-border drop zone (workspace logo)
 *
 * Eager upload model: as soon as the user picks a file, `onUpload(file)`
 * fires and the parent's mutation handles the POST. The component shows
 * a spinner overlay while `onUpload` is in flight, then the parent's
 * `value` prop flips to the new presigned URL and the preview updates
 * automatically.
 *
 * The component is purely presentation + validation; persistence lives
 * in the caller's mutation hook.
 */
export function ImageUploader({
  value,
  onUpload,
  onRemove,
  shape,
  size = 80,
  label,
  helpText,
}: {
  value: string | null | undefined;
  onUpload: (file: File) => Promise<unknown>;
  onRemove?: () => Promise<unknown> | void;
  shape: "circle" | "square";
  size?: number;
  label: string;
  helpText?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const validate = (file: File): string | null => {
    if (!file.type.startsWith("image/")) return "Image files only.";
    if (file.size > MAX_BYTES) return "File too large (max 5 MB).";
    return null;
  };

  const handleFile = async (file: File) => {
    const err = validate(file);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setPending(true);
    try {
      await onUpload(file);
    } catch (e) {
      setError((e as Error).message ?? "Upload failed.");
    } finally {
      setPending(false);
    }
  };

  const onPick = () => inputRef.current?.click();
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = "";
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const hasImage = !!value;

  if (shape === "circle") {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onPick}
            disabled={pending}
            aria-label={label}
            className={cn(
              "relative shrink-0 rounded-full overflow-hidden border border-border bg-muted/40",
              "inline-flex items-center justify-center text-muted-foreground",
              "hover:border-primary hover:text-primary transition-colors",
              "disabled:opacity-60 disabled:cursor-progress",
            )}
            style={{ width: size, height: size }}
          >
            {hasImage ? (
              <img
                src={value!}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <ImagePlus className="h-6 w-6" />
            )}
            {pending && (
              <span className="absolute inset-0 bg-black/40 inline-flex items-center justify-center">
                <Loader2 className="h-5 w-5 text-white animate-spin" />
              </span>
            )}
          </button>
          <div className="flex flex-col gap-1.5">
            <button
              type="button"
              onClick={onPick}
              disabled={pending}
              className="text-xs font-medium text-primary hover:underline disabled:opacity-60 self-start"
            >
              {hasImage ? "Replace" : "Upload image"}
            </button>
            {hasImage && onRemove && (
              <button
                type="button"
                onClick={() => onRemove()}
                disabled={pending}
                className="text-xs text-muted-foreground hover:text-destructive self-start"
              >
                Remove
              </button>
            )}
            {helpText && !error && (
              <p className="text-xs text-muted-foreground">{helpText}</p>
            )}
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={onChange}
          className="hidden"
        />
      </div>
    );
  }

  // square / drop zone
  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={onPick}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        disabled={pending}
        aria-label={label}
        className={cn(
          "relative w-full rounded-lg border border-dashed transition-colors",
          "inline-flex items-center justify-center gap-2 text-muted-foreground",
          "hover:border-primary hover:text-primary",
          dragOver
            ? "border-primary bg-primary-light/40 text-primary"
            : "border-border",
          "disabled:opacity-60 disabled:cursor-progress",
          hasImage ? "p-0 overflow-hidden" : "py-6 px-4",
        )}
        style={{ minHeight: size }}
      >
        {hasImage ? (
          <img
            src={value!}
            alt=""
            className="w-full h-full object-contain"
            style={{ maxHeight: size * 1.5 }}
          />
        ) : (
          <>
            <Upload className="h-4 w-4" />
            <span className="text-xs font-medium">
              Drop an image or click to upload
            </span>
          </>
        )}
        {pending && (
          <span className="absolute inset-0 bg-card/70 inline-flex items-center justify-center rounded-lg">
            <Loader2 className="h-5 w-5 text-primary animate-spin" />
          </span>
        )}
      </button>
      {hasImage && onRemove && (
        <button
          type="button"
          onClick={() => onRemove()}
          disabled={pending}
          className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
        >
          <X className="h-3 w-3" /> Remove
        </button>
      )}
      {helpText && !error && (
        <p className="text-xs text-muted-foreground">{helpText}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onChange}
        className="hidden"
      />
    </div>
  );
}
